<template>
  <div class="min-h-full bg-transparent text-slate-800 dark:text-[#F1F5F9] pb-16 transition-colors duration-200">
    <div class="max-w-7xl mx-auto space-y-6">
      
      <!-- ================= TOP HEADER SECTION ================= -->
      <div class="bg-white dark:bg-[#111C30] rounded-2xl border border-slate-200/80 dark:border-[#263449] p-5 sm:p-6 shadow-sm">
        <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
          <div>
            <div class="flex items-center gap-2 text-xs font-semibold text-blue-600 dark:text-blue-400 mb-1.5 uppercase tracking-wider">
              <ShieldCheck class="w-4 h-4" />
              <span>System Support & Hardware Operations</span>
            </div>
            <h1 class="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 dark:text-[#F1F5F9]">
              School Device & Smart Card Operations
            </h1>
            <p class="text-xs sm:text-sm text-slate-500 dark:text-[#A6B4C8] mt-1">
              Centralized monitoring, diagnostics, and allocation of attendance terminals and smart ID cards by institution
            </p>
          </div>

          <!-- Global Controls: School Selector & Refresh -->
          <div class="flex flex-wrap items-center gap-3">
            <!-- School Filter Dropdown -->
            <div class="relative min-w-[220px] flex-1 sm:flex-none">
              <Building2 class="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-[#7E8DA5] pointer-events-none" />
              <select
                v-model="selectedSchoolId"
                @change="handleSchoolChange"
                class="w-full pl-10 pr-8 py-2.5 text-xs sm:text-sm bg-slate-50 dark:bg-[#172235] border border-slate-200 dark:border-[#263449] rounded-xl text-slate-800 dark:text-[#F1F5F9] focus:outline-none focus:ring-2 focus:ring-blue-500 transition cursor-pointer appearance-none"
              >
                <option value="">All Schools (Global View)</option>
                <option
                  v-for="school in schools"
                  :key="school._id"
                  :value="school._id"
                >
                  {{ school.schoolName }}
                </option>
              </select>
              <div class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                <Filter class="w-3.5 h-3.5" />
              </div>
            </div>

            <!-- Refresh Button -->
            <button
              @click="refreshActiveData"
              :disabled="isLoading"
              type="button"
              class="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 active:scale-[0.98] disabled:opacity-60 disabled:pointer-events-none text-white font-semibold text-xs sm:text-sm shadow-xs transition cursor-pointer"
              title="Refresh current data"
            >
              <RotateCw :class="['w-4 h-4', isLoading ? 'animate-spin' : '']" />
              <span>{{ isLoading ? "Refreshing..." : "Refresh" }}</span>
            </button>
          </div>
        </div>

        <!-- Global Status & Scope Banner -->
        <div class="mt-4 pt-4 border-t border-slate-100 dark:border-[#263449] flex flex-wrap items-center justify-between gap-3 text-xs">
          <div class="flex items-center gap-2">
            <span class="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
            <span class="text-slate-600 dark:text-[#A6B4C8]">
              Current Scope:
              <strong class="text-slate-900 dark:text-[#F1F5F9]">
                {{ selectedSchoolName }}
              </strong>
            </span>
          </div>
          <div class="text-slate-400 dark:text-[#7E8DA5] flex items-center gap-4">
            <span>Devices: <strong>{{ totalDevices }}</strong></span>
            <span>•</span>
            <span>Smart Cards: <strong>{{ totalCardsCount }}</strong></span>
            <span>•</span>
            <span>Last Sync: <strong>{{ lastSyncTime }}</strong></span>
          </div>
        </div>
      </div>

      <!-- ================= TAB SWITCHER ================= -->
      <div class="flex justify-center">
        <div class="w-full max-w-xl grid grid-cols-2 p-1.5 bg-white dark:bg-[#111C30] rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-sm gap-2">
          <!-- Devices Tab Button -->
          <button
            @click="switchTab('devices')"
            type="button"
            :class="[
              'flex items-center justify-center gap-2.5 py-2.5 px-4 rounded-xl text-xs sm:text-sm font-bold transition-all duration-150 cursor-pointer',
              activeTab === 'devices'
                ? 'bg-blue-600 text-white shadow-md shadow-blue-500/20'
                : 'text-slate-600 dark:text-[#A6B4C8] hover:text-slate-900 dark:hover:text-[#F1F5F9] hover:bg-slate-50 dark:hover:bg-[#18243A]',
            ]"
          >
            <Cpu class="w-4 h-4 flex-shrink-0" />
            <span>Attendance Devices</span>
            <span
              :class="[
                'text-[11px] px-2 py-0.5 rounded-full font-semibold',
                activeTab === 'devices'
                  ? 'bg-white/20 text-white'
                  : 'bg-slate-100 dark:bg-[#18243A] text-slate-600 dark:text-[#A6B4C8]',
              ]"
            >
              {{ totalDevices }}
            </span>
          </button>

          <!-- Smart Cards Tab Button -->
          <button
            @click="switchTab('smartcards')"
            type="button"
            :class="[
              'flex items-center justify-center gap-2.5 py-2.5 px-4 rounded-xl text-xs sm:text-sm font-bold transition-all duration-150 cursor-pointer',
              activeTab === 'smartcards'
                ? 'bg-blue-600 text-white shadow-md shadow-blue-500/20'
                : 'text-slate-600 dark:text-[#A6B4C8] hover:text-slate-900 dark:hover:text-[#F1F5F9] hover:bg-slate-50 dark:hover:bg-[#18243A]',
            ]"
          >
            <CreditCard class="w-4 h-4 flex-shrink-0" />
            <span>Smart ID Cards</span>
            <span
              :class="[
                'text-[11px] px-2 py-0.5 rounded-full font-semibold',
                activeTab === 'smartcards'
                  ? 'bg-white/20 text-white'
                  : 'bg-slate-100 dark:bg-[#18243A] text-slate-600 dark:text-[#A6B4C8]',
              ]"
            >
              {{ totalCardsCount }}
            </span>
          </button>
        </div>
      </div>

      <!-- ================= ERROR ALERT ================= -->
      <div
        v-if="errorMessage"
        class="p-4 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/50 rounded-2xl flex items-center justify-between text-xs sm:text-sm text-red-700 dark:text-red-400 shadow-sm"
      >
        <div class="flex items-center gap-2.5">
          <AlertCircle class="w-5 h-5 flex-shrink-0" />
          <span>{{ errorMessage }}</span>
        </div>
        <button
          @click="errorMessage = ''"
          class="text-red-600 hover:text-red-800 dark:text-red-400 p-1 rounded-lg"
        >
          <X class="w-4 h-4" />
        </button>
      </div>

      <!-- ================= TAB 1: DEVICES VIEW ================= -->
      <div v-if="activeTab === 'devices'" class="space-y-6">
        <!-- Devices Quick Stats Cards -->
        <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3.5">
          <!-- Total Devices -->
          <div class="bg-white dark:bg-[#111C30] rounded-2xl p-4 border border-slate-200/80 dark:border-[#263449] shadow-sm flex items-center gap-3.5">
            <div class="w-11 h-11 rounded-xl bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 flex items-center justify-center flex-shrink-0">
              <Cpu class="w-5 h-5" />
            </div>
            <div class="min-w-0">
              <span class="block text-xl sm:text-2xl font-bold tracking-tight text-slate-900 dark:text-[#F1F5F9]">
                {{ totalDevices }}
              </span>
              <span class="text-[11px] font-semibold text-slate-400 dark:text-[#7E8DA5] uppercase tracking-wider">
                Total Devices
              </span>
            </div>
          </div>

          <!-- Online Now -->
          <div class="bg-white dark:bg-[#111C30] rounded-2xl p-4 border border-slate-200/80 dark:border-[#263449] shadow-sm flex items-center gap-3.5">
            <div class="w-11 h-11 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center flex-shrink-0">
              <Wifi class="w-5 h-5" />
            </div>
            <div class="min-w-0">
              <div class="flex items-center gap-1.5">
                <span class="block text-xl sm:text-2xl font-bold tracking-tight text-slate-900 dark:text-[#F1F5F9]">
                  {{ deviceStats.online }}
                </span>
                <span class="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              </div>
              <span class="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">
                Online (Live)
              </span>
            </div>
          </div>

          <!-- Active State -->
          <div class="bg-white dark:bg-[#111C30] rounded-2xl p-4 border border-slate-200/80 dark:border-[#263449] shadow-sm flex items-center gap-3.5">
            <div class="w-11 h-11 rounded-xl bg-sky-50 dark:bg-sky-950/40 text-sky-600 dark:text-sky-400 flex items-center justify-center flex-shrink-0">
              <CheckCircle class="w-5 h-5" />
            </div>
            <div class="min-w-0">
              <span class="block text-xl sm:text-2xl font-bold tracking-tight text-slate-900 dark:text-[#F1F5F9]">
                {{ deviceStats.active }}
              </span>
              <span class="text-[11px] font-semibold text-sky-600 dark:text-sky-400 uppercase tracking-wider">
                Installed & Active
              </span>
            </div>
          </div>

          <!-- Pending Pairing -->
          <div class="bg-white dark:bg-[#111C30] rounded-2xl p-4 border border-slate-200/80 dark:border-[#263449] shadow-sm flex items-center gap-3.5">
            <div class="w-11 h-11 rounded-xl bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 flex items-center justify-center flex-shrink-0">
              <Clock class="w-5 h-5" />
            </div>
            <div class="min-w-0">
              <span class="block text-xl sm:text-2xl font-bold tracking-tight text-slate-900 dark:text-[#F1F5F9]">
                {{ deviceStats.pending }}
              </span>
              <span class="text-[11px] font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wider">
                Pending Setup
              </span>
            </div>
          </div>

          <!-- Disabled / Inactive -->
          <div class="bg-white dark:bg-[#111C30] rounded-2xl p-4 border border-slate-200/80 dark:border-[#263449] shadow-sm flex items-center gap-3.5 col-span-2 sm:col-span-1">
            <div class="w-11 h-11 rounded-xl bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400 flex items-center justify-center flex-shrink-0">
              <XCircle class="w-5 h-5" />
            </div>
            <div class="min-w-0">
              <span class="block text-xl sm:text-2xl font-bold tracking-tight text-slate-900 dark:text-[#F1F5F9]">
                {{ deviceStats.disabled }}
              </span>
              <span class="text-[11px] font-semibold text-rose-600 dark:text-rose-400 uppercase tracking-wider">
                Disabled
              </span>
            </div>
          </div>
        </div>

        <!-- Filter & Search Controls Bar -->
        <div class="bg-white dark:bg-[#111C30] rounded-2xl p-4 border border-slate-200/80 dark:border-[#263449] shadow-sm">
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3 items-center">
            <!-- Search Query -->
            <div class="relative lg:col-span-5">
              <Search class="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-[#7E8DA5] w-4 h-4 pointer-events-none" />
              <input
                v-model="deviceSearchQuery"
                @input="handleDeviceSearch"
                type="text"
                placeholder="Search by device name, chip ID, MAC, code, IP..."
                class="w-full py-2.5 pl-10 pr-9 text-xs sm:text-sm bg-slate-50 dark:bg-[#172235] border border-slate-200 dark:border-[#263449] rounded-xl text-slate-800 dark:text-[#F1F5F9] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
              />
              <button
                v-if="deviceSearchQuery"
                @click="clearDeviceSearch"
                class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-white"
              >
                <X class="w-3.5 h-3.5" />
              </button>
            </div>

            <!-- Status Filter -->
            <div class="lg:col-span-3">
              <select
                v-model="deviceStatusFilter"
                @change="handleDeviceFilterChange"
                class="w-full py-2.5 px-3 text-xs sm:text-sm bg-slate-50 dark:bg-[#172235] border border-slate-200 dark:border-[#263449] rounded-xl text-slate-700 dark:text-[#F1F5F9] focus:outline-none focus:ring-2 focus:ring-blue-500 transition cursor-pointer"
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>

            <!-- Online Filter -->
            <div class="lg:col-span-2">
              <select
                v-model="deviceOnlineFilter"
                class="w-full py-2.5 px-3 text-xs sm:text-sm bg-slate-50 dark:bg-[#172235] border border-slate-200 dark:border-[#263449] rounded-xl text-slate-700 dark:text-[#F1F5F9] focus:outline-none focus:ring-2 focus:ring-blue-500 transition cursor-pointer"
              >
                <option value="all">All States</option>
                <option value="online">Online Only</option>
                <option value="offline">Offline Only</option>
              </select>
            </div>

            <!-- Reset Filters -->
            <div class="lg:col-span-2 flex items-center justify-end">
              <button
                @click="resetDeviceFilters"
                type="button"
                class="w-full py-2.5 px-3 bg-slate-100 dark:bg-[#18243A] hover:bg-slate-200 dark:hover:bg-[#202e48] text-slate-600 dark:text-[#A6B4C8] rounded-xl text-xs font-semibold tracking-wide transition flex items-center justify-center gap-1.5 border border-slate-200/60 dark:border-[#263449]"
              >
                <XCircle class="w-3.5 h-3.5" />
                <span>Reset Filters</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Devices Loading State -->
        <div
          v-if="isLoadingDevices"
          class="py-16 bg-white dark:bg-[#111C30] rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-sm flex items-center justify-center"
        >
          <AppLoader
            variant="card"
            size="md"
            message="Fetching Device Records..."
            subtext="Connecting to hardware controllers and terminal monitors"
            color="indigo"
          />
        </div>

        <!-- Devices List / Table View -->
        <div v-else-if="filteredDevices.length > 0" class="space-y-4">
          <!-- Desktop Table View -->
          <div class="hidden lg:block bg-white dark:bg-[#111C30] rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-sm overflow-hidden">
            <div class="overflow-x-auto">
              <table class="w-full text-left border-collapse text-xs sm:text-sm">
                <thead>
                  <tr class="bg-slate-50/80 dark:bg-[#172235]/60 border-b border-slate-200 dark:border-[#263449] text-slate-600 dark:text-[#A6B4C8]">
                    <th class="px-4 py-3.5 font-bold uppercase tracking-wider text-[11px]">Device & School</th>
                    <th class="px-4 py-3.5 font-bold uppercase tracking-wider text-[11px]">Pairing Code</th>
                    <th class="px-4 py-3.5 font-bold uppercase tracking-wider text-[11px]">Live Status</th>
                    <th class="px-4 py-3.5 font-bold uppercase tracking-wider text-[11px]">Network & MAC</th>
                    <th class="px-4 py-3.5 font-bold uppercase tracking-wider text-[11px]">Chip ID</th>
                    <th class="px-4 py-3.5 font-bold uppercase tracking-wider text-[11px]">Last Ping</th>
                    <th class="px-4 py-3.5 font-bold uppercase tracking-wider text-[11px]">Installed</th>
                    <th class="px-4 py-3.5 font-bold uppercase tracking-wider text-[11px] text-right">Actions</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-100 dark:divide-[#263449]/60">
                  <tr
                    v-for="device in filteredDevices"
                    :key="device._id"
                    class="hover:bg-slate-50/60 dark:hover:bg-[#18243A]/40 transition group"
                  >
                    <!-- Device & School -->
                    <td class="px-4 py-3.5">
                      <div class="font-bold text-slate-900 dark:text-[#F1F5F9]">
                        {{ device.deviceName }}
                      </div>
                      <div class="flex items-center gap-1.5 mt-0.5">
                        <span class="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-slate-100 dark:bg-[#18243A] text-slate-600 dark:text-[#A6B4C8] border border-slate-200/60 dark:border-[#263449]">
                          <Building2 class="w-2.5 h-2.5 mr-1 text-slate-400" />
                          {{ getSchoolName(device.schoolId) }}
                        </span>
                      </div>
                    </td>

                    <!-- Pairing Code -->
                    <td class="px-4 py-3.5">
                      <div class="inline-flex items-center gap-1.5 px-2 py-1 bg-slate-100 dark:bg-[#172235] rounded-md font-mono text-xs font-semibold text-slate-700 dark:text-[#F1F5F9]">
                        <span>{{ device.code || "—" }}</span>
                        <button
                          v-if="device.code"
                          @click="copyText(device.code, 'Pairing Code')"
                          class="text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition"
                          title="Copy Code"
                        >
                          <Check v-if="copiedItem === device.code" class="w-3.5 h-3.5 text-emerald-500" />
                          <Copy v-else class="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>

                    <!-- Live Status + Badge -->
                    <td class="px-4 py-3.5">
                      <div class="flex flex-col gap-1 items-start">
                        <!-- Online pill -->
                        <span
                          :class="[
                            'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide',
                            isDeviceOnline(device)
                              ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/40'
                              : 'bg-slate-100 dark:bg-[#18243A] text-slate-500 dark:text-[#7E8DA5] border border-slate-200 dark:border-[#263449]',
                          ]"
                        >
                          <span
                            :class="[
                              'w-1.5 h-1.5 rounded-full',
                              isDeviceOnline(device) ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400',
                            ]"
                          ></span>
                          {{ isDeviceOnline(device) ? "Online" : "Offline" }}
                        </span>

                        <!-- Status pill -->
                        <span
                          :class="[
                            'px-2 py-0.5 rounded text-[10px] font-semibold uppercase',
                            getStatusBadgeClass(device.status),
                          ]"
                        >
                          {{ device.status }}
                        </span>
                      </div>
                    </td>

                    <!-- Network & MAC -->
                    <td class="px-4 py-3.5">
                      <div class="space-y-0.5 font-mono text-xs text-slate-600 dark:text-[#A6B4C8]">
                        <div class="flex items-center gap-1.5">
                          <span class="text-slate-400 text-[10px]">IP:</span>
                          <span>{{ device.lastIp || "—" }}</span>
                        </div>
                        <div class="flex items-center gap-1.5">
                          <span class="text-slate-400 text-[10px]">MAC:</span>
                          <span class="truncate max-w-[130px]">{{ device.macAddress || "—" }}</span>
                          <button
                            v-if="device.macAddress"
                            @click="copyText(device.macAddress, 'MAC Address')"
                            class="text-slate-400 hover:text-blue-600 dark:hover:text-blue-400"
                            title="Copy MAC"
                          >
                            <Copy class="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </td>

                    <!-- Chip ID -->
                    <td class="px-4 py-3.5">
                      <div class="inline-flex items-center gap-1 font-mono text-xs text-slate-700 dark:text-[#F1F5F9] max-w-[140px] truncate">
                        <span>{{ device.chipId || "—" }}</span>
                        <button
                          v-if="device.chipId"
                          @click="copyText(device.chipId, 'Chip ID')"
                          class="text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 ml-1"
                          title="Copy Chip ID"
                        >
                          <Copy class="w-3 h-3" />
                        </button>
                      </div>
                    </td>

                    <!-- Last Ping -->
                    <td class="px-4 py-3.5">
                      <div class="text-xs text-slate-700 dark:text-[#F1F5F9] font-medium" :title="formatDateFull(device.lastPing)">
                        {{ formatRelativeTime(device.lastPing) }}
                      </div>
                      <div class="text-[10px] text-slate-400 dark:text-[#7E8DA5]">
                        {{ formatDateShort(device.lastPing) }}
                      </div>
                    </td>

                    <!-- Active state -->
                    <td class="px-4 py-3.5">
                      <span
                        :class="[
                          'px-2 py-0.5 rounded text-[11px] font-semibold',
                          device.isActive
                            ? 'bg-emerald-100 dark:bg-emerald-950/50 text-emerald-800 dark:text-emerald-300'
                            : 'bg-slate-100 dark:bg-[#18243A] text-slate-600 dark:text-[#7E8DA5]',
                        ]"
                      >
                        {{ device.isActive ? "Yes" : "No" }}
                      </span>
                    </td>

                    <!-- Actions -->
                    <td class="px-4 py-3.5 text-right">
                      <div class="inline-flex items-center gap-1.5">
                        <button
                          @click="openInspectModal(device)"
                          class="px-2.5 py-1 text-xs font-semibold bg-slate-100 hover:bg-slate-200 dark:bg-[#18243A] dark:hover:bg-[#202e48] text-slate-700 dark:text-[#F1F5F9] rounded-lg transition"
                          title="Inspect Technical Specs"
                        >
                          <Eye class="w-3.5 h-3.5" />
                        </button>
                        <button
                          @click="openEditModal(device)"
                          class="px-3 py-1 text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition flex items-center gap-1 shadow-sm"
                        >
                          <Edit3 class="w-3 h-3" />
                          <span>Edit</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Mobile / Tablet Responsive Cards View -->
          <div class="lg:hidden grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div
              v-for="device in filteredDevices"
              :key="device._id"
              class="bg-white dark:bg-[#111C30] border border-slate-200/80 dark:border-[#263449] rounded-2xl shadow-sm p-4 hover:shadow-md transition flex flex-col justify-between"
            >
              <div>
                <!-- Top Status Header -->
                <div class="flex items-start justify-between pb-3 border-b border-slate-100 dark:border-[#263449] mb-3">
                  <div>
                    <h3 class="font-bold text-slate-900 dark:text-[#F1F5F9] text-sm">
                      {{ device.deviceName }}
                    </h3>
                    <div class="flex items-center gap-1.5 mt-1 font-mono text-xs text-slate-500 dark:text-[#A6B4C8]">
                      <span>Code: {{ device.code || "—" }}</span>
                      <button
                        v-if="device.code"
                        @click="copyText(device.code, 'Pairing Code')"
                        class="text-slate-400 hover:text-blue-600"
                      >
                        <Copy class="w-3 h-3" />
                      </button>
                    </div>
                  </div>

                  <div class="flex flex-col items-end gap-1">
                    <span
                      :class="[
                        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase',
                        isDeviceOnline(device)
                          ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800'
                          : 'bg-slate-100 dark:bg-[#18243A] text-slate-500',
                      ]"
                    >
                      <span :class="['w-1.5 h-1.5 rounded-full', isDeviceOnline(device) ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400']"></span>
                      {{ isDeviceOnline(device) ? "Online" : "Offline" }}
                    </span>
                    <span :class="['px-2 py-0.5 rounded text-[10px] font-semibold uppercase', getStatusBadgeClass(device.status)]">
                      {{ device.status }}
                    </span>
                  </div>
                </div>

                <!-- Info Grid -->
                <div class="space-y-2 text-xs">
                  <div class="flex justify-between items-center">
                    <span class="text-slate-500 dark:text-[#7E8DA5]">School:</span>
                    <span class="text-slate-900 dark:text-[#F1F5F9] font-medium truncate max-w-[180px]">
                      {{ getSchoolName(device.schoolId) }}
                    </span>
                  </div>
                  <div class="flex justify-between items-center">
                    <span class="text-slate-500 dark:text-[#7E8DA5]">Last IP:</span>
                    <span class="font-mono text-slate-800 dark:text-[#F1F5F9]">{{ device.lastIp || "—" }}</span>
                  </div>
                  <div class="flex justify-between items-center">
                    <span class="text-slate-500 dark:text-[#7E8DA5]">MAC:</span>
                    <span class="font-mono text-slate-800 dark:text-[#F1F5F9] truncate max-w-[150px]">{{ device.macAddress || "—" }}</span>
                  </div>
                  <div class="flex justify-between items-center">
                    <span class="text-slate-500 dark:text-[#7E8DA5]">Chip ID:</span>
                    <span class="font-mono text-slate-800 dark:text-[#F1F5F9] truncate max-w-[150px]">{{ device.chipId || "—" }}</span>
                  </div>
                  <div class="flex justify-between items-center">
                    <span class="text-slate-500 dark:text-[#7E8DA5]">Last Ping:</span>
                    <span class="text-slate-800 dark:text-[#F1F5F9] font-medium">{{ formatRelativeTime(device.lastPing) }}</span>
                  </div>
                </div>
              </div>

              <!-- Card Actions -->
              <div class="mt-4 pt-3 border-t border-slate-100 dark:border-[#263449] flex gap-2">
                <button
                  @click="openInspectModal(device)"
                  class="flex-1 py-2 text-xs font-semibold bg-slate-100 hover:bg-slate-200 dark:bg-[#18243A] dark:hover:bg-[#202e48] text-slate-700 dark:text-[#F1F5F9] rounded-xl transition flex items-center justify-center gap-1.5"
                >
                  <Eye class="w-3.5 h-3.5" />
                  <span>Inspect</span>
                </button>
                <button
                  @click="openEditModal(device)"
                  class="flex-1 py-2 text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition flex items-center justify-center gap-1.5 shadow-sm"
                >
                  <Edit3 class="w-3.5 h-3.5" />
                  <span>Edit</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Devices Pagination -->
          <div class="bg-white dark:bg-[#111C30] rounded-2xl p-4 border border-slate-200/80 dark:border-[#263449] shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4">
            <p class="text-xs sm:text-sm text-slate-500 dark:text-[#A6B4C8] order-2 sm:order-1">
              Showing <strong>{{ (deviceCurrentPage - 1) * deviceLimit + 1 }}</strong> to
              <strong>{{ Math.min(deviceCurrentPage * deviceLimit, totalDevices) }}</strong> of
              <strong>{{ totalDevices }}</strong> devices
            </p>

            <div class="flex items-center gap-2 order-1 sm:order-2 w-full sm:w-auto justify-end">
              <button
                @click="changeDevicePage(deviceCurrentPage - 1)"
                :disabled="deviceCurrentPage === 1"
                class="px-3.5 py-1.5 text-xs font-semibold bg-slate-100 hover:bg-slate-200 dark:bg-[#18243A] dark:hover:bg-[#202e48] text-slate-700 dark:text-[#F1F5F9] rounded-xl disabled:opacity-40 disabled:cursor-not-allowed transition flex items-center gap-1"
              >
                <ChevronLeft class="w-4 h-4" />
                <span>Prev</span>
              </button>
              <span class="px-3 py-1.5 text-xs font-semibold text-slate-700 dark:text-[#F1F5F9] bg-slate-50 dark:bg-[#172235] border border-slate-200 dark:border-[#263449] rounded-xl">
                Page {{ deviceCurrentPage }} of {{ totalDevicePages }}
              </span>
              <button
                @click="changeDevicePage(deviceCurrentPage + 1)"
                :disabled="deviceCurrentPage >= totalDevicePages"
                class="px-3.5 py-1.5 text-xs font-semibold bg-slate-100 hover:bg-slate-200 dark:bg-[#18243A] dark:hover:bg-[#202e48] text-slate-700 dark:text-[#F1F5F9] rounded-xl disabled:opacity-40 disabled:cursor-not-allowed transition flex items-center gap-1"
              >
                <span>Next</span>
                <ChevronRight class="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <!-- Devices Empty State -->
        <div
          v-else
          class="text-center py-16 bg-white dark:bg-[#111C30] rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-sm p-6"
        >
          <div class="w-14 h-14 rounded-2xl bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 flex items-center justify-center mx-auto mb-3">
            <Cpu class="w-7 h-7" />
          </div>
          <h3 class="text-base font-bold text-slate-900 dark:text-[#F1F5F9] mb-1">
            No Attendance Devices Found
          </h3>
          <p class="text-xs text-slate-400 dark:text-[#7E8DA5] max-w-md mx-auto mb-4">
            No devices matched the selected school or search criteria. Try selecting "All Schools" or clearing filters.
          </p>
          <button
            @click="resetDeviceFilters"
            type="button"
            class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-xl transition shadow-sm"
          >
            Clear Active Filters
          </button>
        </div>
      </div>

      <!-- ================= TAB 2: SMART CARDS VIEW ================= -->
      <div v-else-if="activeTab === 'smartcards'" class="space-y-6">
        <!-- Smart Cards Quick Stats Cards -->
        <div class="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3.5">
          <!-- Total Cards -->
          <div class="bg-white dark:bg-[#111C30] rounded-2xl p-4 border border-slate-200/80 dark:border-[#263449] shadow-sm flex items-center gap-3">
            <div class="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 flex items-center justify-center flex-shrink-0">
              <CreditCard class="w-5 h-5" />
            </div>
            <div class="min-w-0">
              <span class="block text-xl font-bold tracking-tight text-slate-900 dark:text-[#F1F5F9]">
                {{ totalCardsCount }}
              </span>
              <span class="text-[10px] font-semibold text-slate-400 dark:text-[#7E8DA5] uppercase tracking-wider">
                Total Cards
              </span>
            </div>
          </div>

          <!-- Active Cards -->
          <div class="bg-white dark:bg-[#111C30] rounded-2xl p-4 border border-slate-200/80 dark:border-[#263449] shadow-sm flex items-center gap-3">
            <div class="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center flex-shrink-0">
              <CheckCircle class="w-5 h-5" />
            </div>
            <div class="min-w-0">
              <span class="block text-xl font-bold tracking-tight text-slate-900 dark:text-[#F1F5F9]">
                {{ cardStats.active }}
              </span>
              <span class="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">
                Active
              </span>
            </div>
          </div>

          <!-- In Transit -->
          <div class="bg-white dark:bg-[#111C30] rounded-2xl p-4 border border-slate-200/80 dark:border-[#263449] shadow-sm flex items-center gap-3">
            <div class="w-10 h-10 rounded-xl bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 flex items-center justify-center flex-shrink-0">
              <Truck class="w-5 h-5" />
            </div>
            <div class="min-w-0">
              <span class="block text-xl font-bold tracking-tight text-slate-900 dark:text-[#F1F5F9]">
                {{ cardStats.inTransit }}
              </span>
              <span class="text-[10px] font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wider">
                In Transit
              </span>
            </div>
          </div>

          <!-- Ordered Again -->
          <div class="bg-white dark:bg-[#111C30] rounded-2xl p-4 border border-slate-200/80 dark:border-[#263449] shadow-sm flex items-center gap-3">
            <div class="w-10 h-10 rounded-xl bg-purple-50 dark:bg-purple-950/40 text-purple-600 dark:text-purple-400 flex items-center justify-center flex-shrink-0">
              <RotateCw class="w-5 h-5" />
            </div>
            <div class="min-w-0">
              <span class="block text-xl font-bold tracking-tight text-slate-900 dark:text-[#F1F5F9]">
                {{ cardStats.orderedAgain }}
              </span>
              <span class="text-[10px] font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wider">
                Ordered Again
              </span>
            </div>
          </div>

          <!-- Disabled Cards -->
          <div class="bg-white dark:bg-[#111C30] rounded-2xl p-4 border border-slate-200/80 dark:border-[#263449] shadow-sm flex items-center gap-3">
            <div class="w-10 h-10 rounded-xl bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400 flex items-center justify-center flex-shrink-0">
              <XCircle class="w-5 h-5" />
            </div>
            <div class="min-w-0">
              <span class="block text-xl font-bold tracking-tight text-slate-900 dark:text-[#F1F5F9]">
                {{ cardStats.disabled }}
              </span>
              <span class="text-[10px] font-semibold text-rose-600 dark:text-rose-400 uppercase tracking-wider">
                Disabled
              </span>
            </div>
          </div>

          <!-- Students Distribution -->
          <div class="bg-white dark:bg-[#111C30] rounded-2xl p-4 border border-slate-200/80 dark:border-[#263449] shadow-sm flex items-center gap-3">
            <div class="w-10 h-10 rounded-xl bg-sky-50 dark:bg-sky-950/40 text-sky-600 dark:text-sky-400 flex items-center justify-center flex-shrink-0">
              <GraduationCap class="w-5 h-5" />
            </div>
            <div class="min-w-0">
              <span class="block text-xl font-bold tracking-tight text-slate-900 dark:text-[#F1F5F9]">
                {{ cardStats.students }}
              </span>
              <span class="text-[10px] font-semibold text-sky-600 dark:text-sky-400 uppercase tracking-wider">
                Students
              </span>
            </div>
          </div>

          <!-- Teachers Distribution -->
          <div class="bg-white dark:bg-[#111C30] rounded-2xl p-4 border border-slate-200/80 dark:border-[#263449] shadow-sm flex items-center gap-3">
            <div class="w-10 h-10 rounded-xl bg-violet-50 dark:bg-violet-950/40 text-violet-600 dark:text-violet-400 flex items-center justify-center flex-shrink-0">
              <UserCog class="w-5 h-5" />
            </div>
            <div class="min-w-0">
              <span class="block text-xl font-bold tracking-tight text-slate-900 dark:text-[#F1F5F9]">
                {{ cardStats.teachers }}
              </span>
              <span class="text-[10px] font-semibold text-violet-600 dark:text-violet-400 uppercase tracking-wider">
                Teachers
              </span>
            </div>
          </div>
        </div>

        <!-- Smart Card Search & Filter Toolbar -->
        <div class="bg-white dark:bg-[#111C30] rounded-2xl p-4 border border-slate-200/80 dark:border-[#263449] shadow-sm">
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3 items-center">
            <!-- Search Query -->
            <div class="relative lg:col-span-5">
              <Search class="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-[#7E8DA5] w-4 h-4 pointer-events-none" />
              <input
                v-model="cardSearchQuery"
                @input="handleCardSearch"
                type="text"
                placeholder="Search by holder name, card ID, roll number, email..."
                class="w-full py-2.5 pl-10 pr-9 text-xs sm:text-sm bg-slate-50 dark:bg-[#172235] border border-slate-200 dark:border-[#263449] rounded-xl text-slate-800 dark:text-[#F1F5F9] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
              />
              <button
                v-if="cardSearchQuery"
                @click="clearCardSearch"
                class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-white"
              >
                <X class="w-3.5 h-3.5" />
              </button>
            </div>

            <!-- Role Filter -->
            <div class="lg:col-span-3">
              <select
                v-model="cardRoleFilter"
                @change="handleCardFilterChange"
                class="w-full py-2.5 px-3 text-xs sm:text-sm bg-slate-50 dark:bg-[#172235] border border-slate-200 dark:border-[#263449] rounded-xl text-slate-700 dark:text-[#F1F5F9] focus:outline-none focus:ring-2 focus:ring-blue-500 transition cursor-pointer"
              >
                <option value="">All Member Roles</option>
                <option value="student">Students</option>
                <option value="teacher">Teachers</option>
              </select>
            </div>

            <!-- Status Filter -->
            <div class="lg:col-span-2">
              <select
                v-model="cardStatusFilter"
                @change="handleCardFilterChange"
                class="w-full py-2.5 px-3 text-xs sm:text-sm bg-slate-50 dark:bg-[#172235] border border-slate-200 dark:border-[#263449] rounded-xl text-slate-700 dark:text-[#F1F5F9] focus:outline-none focus:ring-2 focus:ring-blue-500 transition cursor-pointer"
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="inTransit">In Transit</option>
                <option value="orderedAgain">Ordered Again</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>

            <!-- Reset Filters -->
            <div class="lg:col-span-2 flex items-center justify-end">
              <button
                @click="resetCardFilters"
                type="button"
                class="w-full py-2.5 px-3 bg-slate-100 dark:bg-[#18243A] hover:bg-slate-200 dark:hover:bg-[#202e48] text-slate-600 dark:text-[#A6B4C8] rounded-xl text-xs font-semibold tracking-wide transition flex items-center justify-center gap-1.5 border border-slate-200/60 dark:border-[#263449]"
              >
                <XCircle class="w-3.5 h-3.5" />
                <span>Reset Filters</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Smart Cards Loading State -->
        <div
          v-if="isLoadingCards"
          class="py-16 bg-white dark:bg-[#111C30] rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-sm flex items-center justify-center"
        >
          <AppLoader
            variant="card"
            size="md"
            message="Fetching Smart Cards..."
            subtext="Retrieving card assignment status and inventory records"
            color="indigo"
          />
        </div>

        <!-- Smart Cards List / Table View -->
        <div v-else-if="smartCards.length > 0" class="space-y-4">
          <!-- Desktop Table View -->
          <div class="hidden lg:block bg-white dark:bg-[#111C30] rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-sm overflow-hidden">
            <div class="overflow-x-auto">
              <table class="w-full text-left border-collapse text-xs sm:text-sm">
                <thead>
                  <tr class="bg-slate-50/80 dark:bg-[#172235]/60 border-b border-slate-200 dark:border-[#263449] text-slate-600 dark:text-[#A6B4C8]">
                    <th class="px-4 py-3.5 font-bold uppercase tracking-wider text-[11px]">Card ID</th>
                    <th class="px-4 py-3.5 font-bold uppercase tracking-wider text-[11px]">Cardholder</th>
                    <th class="px-4 py-3.5 font-bold uppercase tracking-wider text-[11px]">School</th>
                    <th class="px-4 py-3.5 font-bold uppercase tracking-wider text-[11px]">Class & Roll No</th>
                    <th class="px-4 py-3.5 font-bold uppercase tracking-wider text-[11px]">Contact</th>
                    <th class="px-4 py-3.5 font-bold uppercase tracking-wider text-[11px]">Card Status</th>
                    <th class="px-4 py-3.5 font-bold uppercase tracking-wider text-[11px] text-right">Actions</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-100 dark:divide-[#263449]/60">
                  <tr
                    v-for="card in smartCards"
                    :key="card.smartCardId"
                    class="hover:bg-slate-50/60 dark:hover:bg-[#18243A]/40 transition group"
                  >
                    <!-- Card ID -->
                    <td class="px-4 py-3.5">
                      <div class="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 dark:bg-[#172235] rounded-md font-mono text-xs font-bold text-slate-800 dark:text-[#F1F5F9]">
                        <span>#{{ card.smartCardId }}</span>
                        <button
                          @click="copyText(card.smartCardId, 'Smart Card ID')"
                          class="text-slate-400 hover:text-blue-600 dark:hover:text-blue-400"
                          title="Copy Card ID"
                        >
                          <Check v-if="copiedItem === card.smartCardId" class="w-3.5 h-3.5 text-emerald-500" />
                          <Copy v-else class="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>

                    <!-- Cardholder Profile -->
                    <td class="px-4 py-3.5">
                      <div class="flex items-center gap-3">
                        <!-- Avatar -->
                        <div class="w-10 h-10 rounded-xl overflow-hidden ring-1 ring-slate-200 dark:ring-[#263449] bg-slate-100 dark:bg-[#18243A] flex items-center justify-center flex-shrink-0">
                          <img
                            v-if="card.photo && !cardImageErrors[card.smartCardId]"
                            :src="card.photo"
                            :alt="card.name"
                            class="w-full h-full object-cover"
                            @error="cardImageErrors[card.smartCardId] = true"
                          />
                          <div
                            v-else
                            :style="{ backgroundColor: getAvatarColor(card.name) }"
                            class="w-full h-full flex items-center justify-center text-white font-bold text-sm uppercase"
                          >
                            {{ getInitial(card.name) }}
                          </div>
                        </div>

                        <!-- Name & Role -->
                        <div class="min-w-0">
                          <div class="font-bold text-slate-900 dark:text-[#F1F5F9] truncate capitalize">
                            {{ card.name || "Unnamed Member" }}
                          </div>
                          <div class="flex items-center gap-1.5 flex-wrap mt-0.5">
                            <span
                              :class="[
                                'inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider',
                                card.role === 'student'
                                  ? 'bg-sky-50 dark:bg-sky-950/40 text-sky-700 dark:text-sky-400 border border-sky-200 dark:border-sky-800/40'
                                  : 'bg-violet-50 dark:bg-violet-950/40 text-violet-700 dark:text-violet-400 border border-violet-200 dark:border-violet-800/40',
                              ]"
                            >
                              <GraduationCap v-if="card.role === 'student'" class="w-2.5 h-2.5" />
                              <UserCog v-else class="w-2.5 h-2.5" />
                              {{ card.role }}
                            </span>

                            <!-- Re-ordered by admin notice tag -->
                            <span
                              v-if="card.status === 'orderedAgain' || card.isReordered"
                              class="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-purple-50 dark:bg-purple-950/50 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800/40"
                              title="This card is ordered again by the admin"
                            >
                              <RotateCw class="w-2.5 h-2.5" />
                              <span>Ordered again by admin</span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </td>

                    <!-- School Name -->
                    <td class="px-4 py-3.5">
                      <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 dark:bg-[#18243A] text-slate-700 dark:text-[#F1F5F9] border border-slate-200/60 dark:border-[#263449]">
                        <Building2 class="w-3 h-3 mr-1 text-slate-400" />
                        {{ card.schoolName || "—" }}
                      </span>
                    </td>

                    <!-- Class & Roll No -->
                    <td class="px-4 py-3.5">
                      <div v-if="card.role === 'student'" class="space-y-0.5 text-xs text-slate-600 dark:text-[#A6B4C8]">
                        <div v-if="card.className" class="flex items-center gap-1">
                          <BookOpen class="w-3 h-3 text-slate-400" />
                          <span>{{ card.className }}</span>
                        </div>
                        <div v-if="card.rollNumber" class="flex items-center gap-1 font-mono text-[11px]">
                          <Hash class="w-3 h-3 text-slate-400" />
                          <span>Roll: {{ card.rollNumber }}</span>
                        </div>
                        <span v-if="!card.className && !card.rollNumber" class="text-slate-400">—</span>
                      </div>
                      <div v-else class="text-xs text-slate-400 italic">
                        Faculty Staff
                      </div>
                    </td>

                    <!-- Contact -->
                    <td class="px-4 py-3.5">
                      <div class="space-y-0.5 text-xs text-slate-600 dark:text-[#A6B4C8] max-w-[180px]">
                        <div v-if="card.email" class="flex items-center gap-1.5 truncate">
                          <Mail class="w-3 h-3 text-slate-400 flex-shrink-0" />
                          <span class="truncate">{{ card.email }}</span>
                        </div>
                        <div v-if="card.phone" class="flex items-center gap-1.5 truncate">
                          <Phone class="w-3 h-3 text-slate-400 flex-shrink-0" />
                          <span>{{ card.phone }}</span>
                        </div>
                        <span v-if="!card.email && !card.phone" class="text-slate-400">—</span>
                      </div>
                    </td>

                    <!-- Status -->
                    <td class="px-4 py-3.5">
                      <div class="flex flex-col gap-1 items-start">
                        <span
                          :class="[
                            'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold tracking-wide uppercase',
                            card.status === 'active'
                              ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/40'
                              : card.status === 'disabled'
                              ? 'bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-400 border border-rose-200 dark:border-rose-800/40'
                              : card.status === 'orderedAgain' || card.isReordered
                              ? 'bg-purple-50 dark:bg-purple-950/40 text-purple-700 dark:text-purple-400 border border-purple-200 dark:border-purple-800/40'
                              : 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800/40',
                          ]"
                        >
                          <span
                            :class="[
                              'w-1.5 h-1.5 rounded-full',
                              card.status === 'active'
                                ? 'bg-emerald-500 animate-pulse'
                                : card.status === 'disabled'
                                ? 'bg-rose-500'
                                : card.status === 'orderedAgain' || card.isReordered
                                ? 'bg-purple-500 animate-pulse'
                                : 'bg-amber-500',
                            ]"
                          ></span>
                          {{ card.status === 'orderedAgain' || card.isReordered ? 'Ordered Again' : formatCardStatus(card.status) }}
                        </span>

                        <span
                          v-if="card.status === 'orderedAgain' || card.isReordered"
                          class="text-[10px] text-purple-600 dark:text-purple-400 font-semibold"
                        >
                          By Admin
                        </span>
                      </div>
                    </td>

                    <!-- Actions -->
                    <td class="px-4 py-3.5 text-right">
                      <button
                        @click="openCardModal(card)"
                        class="px-3 py-1.5 text-xs font-semibold bg-white hover:bg-blue-600 dark:bg-[#18243A] dark:hover:bg-blue-600 text-slate-700 hover:text-white dark:text-[#F1F5F9] dark:hover:text-white border border-slate-200 dark:border-[#263449] hover:border-transparent rounded-xl transition shadow-sm inline-flex items-center gap-1.5"
                      >
                        <Eye class="w-3.5 h-3.5" />
                        <span>View Digital ID</span>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Mobile / Tablet Responsive Cards View -->
          <div class="lg:hidden grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div
              v-for="card in smartCards"
              :key="card.smartCardId"
              class="bg-white dark:bg-[#111C30] border border-slate-200/80 dark:border-[#263449] rounded-2xl shadow-sm p-4 hover:shadow-md transition flex flex-col justify-between"
            >
              <div>
                <!-- Top Status Bar -->
                <div class="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-[#263449] mb-3">
                  <span class="font-mono text-xs font-bold text-slate-700 dark:text-[#F1F5F9]">
                    #{{ card.smartCardId }}
                  </span>
                  <span
                    :class="[
                      'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase',
                      card.status === 'active'
                        ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400'
                        : card.status === 'disabled'
                        ? 'bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400'
                        : card.status === 'orderedAgain'
                        ? 'bg-purple-50 dark:bg-purple-950/40 text-purple-600 dark:text-purple-400'
                        : 'bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400',
                    ]"
                  >
                    <span :class="['w-1.5 h-1.5 rounded-full', card.status === 'active' ? 'bg-emerald-500' : card.status === 'orderedAgain' ? 'bg-purple-500 animate-pulse' : 'bg-slate-400']"></span>
                    {{ formatCardStatus(card.status) }}
                  </span>
                </div>

                <!-- Profile Information -->
                <div class="flex items-start gap-3 mb-3">
                  <div class="w-12 h-12 rounded-xl overflow-hidden ring-1 ring-slate-200 dark:ring-[#263449] bg-slate-100 dark:bg-[#18243A] flex items-center justify-center flex-shrink-0">
                    <img
                      v-if="card.photo && !cardImageErrors[card.smartCardId]"
                      :src="card.photo"
                      :alt="card.name"
                      class="w-full h-full object-cover"
                      @error="cardImageErrors[card.smartCardId] = true"
                    />
                    <div
                      v-else
                      :style="{ backgroundColor: getAvatarColor(card.name) }"
                      class="w-full h-full flex items-center justify-center text-white font-bold text-base uppercase"
                    >
                      {{ getInitial(card.name) }}
                    </div>
                  </div>

                  <div class="min-w-0 flex-1">
                    <h3 class="font-bold text-slate-900 dark:text-[#F1F5F9] text-sm truncate capitalize">
                      {{ card.name }}
                    </h3>
                    <p class="text-xs text-slate-500 dark:text-[#A6B4C8] truncate">
                      {{ card.schoolName }}
                    </p>
                    <span
                      :class="[
                        'inline-flex items-center px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider mt-1 text-white',
                        card.role === 'student' ? 'bg-blue-600' : 'bg-violet-600',
                      ]"
                    >
                      {{ card.role }}
                    </span>
                  </div>
                </div>

                <!-- Academic / Contact Info -->
                <div class="space-y-1.5 text-xs text-slate-600 dark:text-[#A6B4C8]">
                  <div v-if="card.role === 'student' && (card.className || card.rollNumber)" class="flex justify-between items-center">
                    <span class="text-slate-400">Class/Roll:</span>
                    <span class="font-medium text-slate-800 dark:text-[#F1F5F9]">
                      {{ card.className || "—" }} (Roll: {{ card.rollNumber || "—" }})
                    </span>
                  </div>
                  <div v-if="card.email" class="flex justify-between items-center">
                    <span class="text-slate-400">Email:</span>
                    <span class="truncate max-w-[170px] text-slate-800 dark:text-[#F1F5F9]">{{ card.email }}</span>
                  </div>
                  <div v-if="card.phone" class="flex justify-between items-center">
                    <span class="text-slate-400">Phone:</span>
                    <span class="text-slate-800 dark:text-[#F1F5F9]">{{ card.phone }}</span>
                  </div>
                </div>

                <!-- Re-order Notice -->
                <div
                  v-if="card.status === 'orderedAgain' || card.isReordered"
                  class="mt-2.5 p-2 bg-purple-50 dark:bg-purple-950/30 border border-purple-200/70 dark:border-purple-800/40 rounded-xl text-[11px] text-purple-800 dark:text-purple-300 flex items-center gap-1.5"
                >
                  <RotateCw class="w-3 h-3 text-purple-600 dark:text-purple-400 flex-shrink-0" />
                  <span>This card is ordered again by the admin</span>
                </div>
              </div>

              <!-- Card Action -->
              <div class="mt-4 pt-3 border-t border-slate-100 dark:border-[#263449]">
                <button
                  @click="openCardModal(card)"
                  class="w-full py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-semibold transition flex items-center justify-center gap-1.5 shadow-sm"
                >
                  <Eye class="w-3.5 h-3.5" />
                  <span>View Digital ID</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Smart Cards Pagination -->
          <div class="bg-white dark:bg-[#111C30] rounded-2xl p-4 border border-slate-200/80 dark:border-[#263449] shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4">
            <p class="text-xs sm:text-sm text-slate-500 dark:text-[#A6B4C8] order-2 sm:order-1">
              Showing <strong>{{ (cardCurrentPage - 1) * cardLimit + 1 }}</strong> to
              <strong>{{ Math.min(cardCurrentPage * cardLimit, totalCardsCount) }}</strong> of
              <strong>{{ totalCardsCount }}</strong> smart cards
            </p>

            <div class="flex items-center gap-2 order-1 sm:order-2 w-full sm:w-auto justify-end">
              <button
                @click="changeCardPage(cardCurrentPage - 1)"
                :disabled="cardCurrentPage === 1"
                class="px-3.5 py-1.5 text-xs font-semibold bg-slate-100 hover:bg-slate-200 dark:bg-[#18243A] dark:hover:bg-[#202e48] text-slate-700 dark:text-[#F1F5F9] rounded-xl disabled:opacity-40 disabled:cursor-not-allowed transition flex items-center gap-1"
              >
                <ChevronLeft class="w-4 h-4" />
                <span>Prev</span>
              </button>
              <span class="px-3 py-1.5 text-xs font-semibold text-slate-700 dark:text-[#F1F5F9] bg-slate-50 dark:bg-[#172235] border border-slate-200 dark:border-[#263449] rounded-xl">
                Page {{ cardCurrentPage }} of {{ totalCardPages }}
              </span>
              <button
                @click="changeCardPage(cardCurrentPage + 1)"
                :disabled="cardCurrentPage >= totalCardPages"
                class="px-3.5 py-1.5 text-xs font-semibold bg-slate-100 hover:bg-slate-200 dark:bg-[#18243A] dark:hover:bg-[#202e48] text-slate-700 dark:text-[#F1F5F9] rounded-xl disabled:opacity-40 disabled:cursor-not-allowed transition flex items-center gap-1"
              >
                <span>Next</span>
                <ChevronRight class="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <!-- Smart Cards Empty State -->
        <div
          v-else
          class="text-center py-16 bg-white dark:bg-[#111C30] rounded-2xl border border-slate-200/80 dark:border-[#263449] shadow-sm p-6"
        >
          <div class="w-14 h-14 rounded-2xl bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 flex items-center justify-center mx-auto mb-3">
            <CreditCard class="w-7 h-7" />
          </div>
          <h3 class="text-base font-bold text-slate-900 dark:text-[#F1F5F9] mb-1">
            No Smart Cards Found
          </h3>
          <p class="text-xs text-slate-400 dark:text-[#7E8DA5] max-w-md mx-auto mb-4">
            No smart cards were found for the selected school or search criteria. Try selecting "All Schools" or resetting filters.
          </p>
          <button
            @click="resetCardFilters"
            type="button"
            class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-xl transition shadow-sm"
          >
            Clear Active Filters
          </button>
        </div>
      </div>

      <!-- ================= MODAL 1: EDIT DEVICE MODAL ================= -->
      <div
        v-if="showEditModal"
        class="fixed inset-0 bg-slate-900/60 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 transition-all"
        @click.self="closeEditModal"
      >
        <div
          class="bg-white dark:bg-[#111C30] rounded-2xl shadow-2xl p-6 w-full max-w-lg border border-slate-200 dark:border-[#263449] max-h-[92vh] overflow-y-auto"
        >
          <div class="flex items-center justify-between pb-4 border-b border-slate-100 dark:border-[#263449] mb-5">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                <Edit3 class="w-5 h-5" />
              </div>
              <div>
                <h2 class="text-lg font-bold text-slate-900 dark:text-[#F1F5F9]">
                  Edit Device Configuration
                </h2>
                <p class="text-xs text-slate-500 dark:text-[#A6B4C8]">
                  Update terminal operational parameters and endpoints
                </p>
              </div>
            </div>
            <button
              @click="closeEditModal"
              class="text-slate-400 hover:text-slate-600 dark:hover:text-white p-1 rounded-lg"
            >
              <X class="w-5 h-5" />
            </button>
          </div>

          <form @submit.prevent="saveDevice" class="space-y-4">
            <!-- Device Name -->
            <div>
              <label class="block text-xs font-bold text-slate-700 dark:text-[#F1F5F9] uppercase tracking-wider mb-1.5">
                Device Name <span class="text-red-500">*</span>
              </label>
              <input
                v-model="editForm.deviceName"
                type="text"
                class="w-full px-3.5 py-2.5 text-xs sm:text-sm bg-slate-50 dark:bg-[#172235] border border-slate-200 dark:border-[#263449] rounded-xl text-slate-900 dark:text-[#F1F5F9] focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                required
              />
            </div>

            <!-- Pairing Code (Read-Only) -->
            <div>
              <label class="block text-xs font-bold text-slate-700 dark:text-[#F1F5F9] uppercase tracking-wider mb-1.5">
                Pairing OTP Code
              </label>
              <div class="flex items-center gap-2">
                <input
                  :value="editForm.code"
                  type="text"
                  disabled
                  class="w-full px-3.5 py-2.5 font-mono text-xs sm:text-sm bg-slate-100 dark:bg-[#18243A] border border-slate-200 dark:border-[#263449] rounded-xl text-slate-600 dark:text-[#A6B4C8] cursor-not-allowed"
                />
                <button
                  type="button"
                  @click="copyText(editForm.code, 'Pairing Code')"
                  class="px-3 py-2.5 bg-slate-100 dark:bg-[#18243A] border border-slate-200 dark:border-[#263449] rounded-xl text-slate-600 dark:text-[#A6B4C8] hover:text-blue-600"
                >
                  <Copy class="w-4 h-4" />
                </button>
              </div>
            </div>

            <!-- Status Selector -->
            <div>
              <label class="block text-xs font-bold text-slate-700 dark:text-[#F1F5F9] uppercase tracking-wider mb-1.5">
                Terminal Status
              </label>
              <select
                v-model="editForm.status"
                class="w-full px-3.5 py-2.5 text-xs sm:text-sm bg-slate-50 dark:bg-[#172235] border border-slate-200 dark:border-[#263449] rounded-xl text-slate-900 dark:text-[#F1F5F9] focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
              >
                <option value="active">Active (Fully Operational)</option>
                <option value="pending">Pending (Awaiting Pairing)</option>
                <option value="disabled">Disabled (Revoke Access)</option>
              </select>
            </div>

            <!-- Student Attendance URL -->
            <div>
              <label class="block text-xs font-bold text-slate-700 dark:text-[#F1F5F9] uppercase tracking-wider mb-1.5">
                Student Attendance Endpoint URL
              </label>
              <input
                v-model="editForm.studentURL"
                type="text"
                placeholder="https://api.domain.com/api/attendance/student"
                class="w-full px-3.5 py-2.5 font-mono text-xs sm:text-sm bg-slate-50 dark:bg-[#172235] border border-slate-200 dark:border-[#263449] rounded-xl text-slate-900 dark:text-[#F1F5F9] focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
              />
            </div>

            <!-- Teacher Attendance URL -->
            <div>
              <label class="block text-xs font-bold text-slate-700 dark:text-[#F1F5F9] uppercase tracking-wider mb-1.5">
                Teacher Attendance Endpoint URL
              </label>
              <input
                v-model="editForm.teacherURL"
                type="text"
                placeholder="https://api.domain.com/api/attendance/teacher"
                class="w-full px-3.5 py-2.5 font-mono text-xs sm:text-sm bg-slate-50 dark:bg-[#172235] border border-slate-200 dark:border-[#263449] rounded-xl text-slate-900 dark:text-[#F1F5F9] focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
              />
            </div>

            <!-- Active Toggle -->
            <div class="pt-2">
              <label class="flex items-center justify-between p-3 bg-slate-50 dark:bg-[#172235] rounded-xl border border-slate-200/80 dark:border-[#263449] cursor-pointer">
                <div>
                  <span class="block text-xs font-bold text-slate-800 dark:text-[#F1F5F9]">Hardware Active State</span>
                  <span class="block text-[11px] text-slate-400">Mark hardware as ready to capture attendance</span>
                </div>
                <input
                  v-model="editForm.isActive"
                  type="checkbox"
                  class="w-4 h-4 accent-blue-600 rounded cursor-pointer"
                />
              </label>
            </div>

            <!-- Actions -->
            <div class="flex items-center gap-3 pt-3">
              <button
                type="button"
                @click="closeEditModal"
                class="flex-1 py-2.5 text-xs sm:text-sm font-semibold bg-slate-100 hover:bg-slate-200 dark:bg-[#18243A] dark:hover:bg-[#202e48] text-slate-700 dark:text-[#A6B4C8] rounded-xl transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                :disabled="isSaving"
                class="flex-1 py-2.5 text-xs sm:text-sm font-semibold bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl transition flex items-center justify-center gap-2 shadow-sm"
              >
                <RotateCw v-if="isSaving" class="w-4 h-4 animate-spin" />
                <span>{{ isSaving ? "Saving..." : "Save Changes" }}</span>
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- ================= MODAL 2: INSPECT DEVICE SPECS ================= -->
      <div
        v-if="showInspectModal && inspectingDevice"
        class="fixed inset-0 bg-slate-900/60 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 transition-all"
        @click.self="closeInspectModal"
      >
        <div class="bg-white dark:bg-[#111C30] rounded-2xl shadow-2xl p-6 w-full max-w-lg border border-slate-200 dark:border-[#263449]">
          <div class="flex items-center justify-between pb-4 border-b border-slate-100 dark:border-[#263449] mb-4">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                <Cpu class="w-5 h-5" />
              </div>
              <div>
                <h2 class="text-lg font-bold text-slate-900 dark:text-[#F1F5F9]">
                  Terminal Hardware Diagnostics
                </h2>
                <p class="text-xs text-slate-500 dark:text-[#A6B4C8]">
                  Detailed technical identifiers & network trace
                </p>
              </div>
            </div>
            <button @click="closeInspectModal" class="text-slate-400 hover:text-slate-600 dark:hover:text-white p-1">
              <X class="w-5 h-5" />
            </button>
          </div>

          <div class="space-y-3 text-xs">
            <div class="p-3 bg-slate-50 dark:bg-[#172235] rounded-xl flex justify-between items-center">
              <span class="text-slate-500 dark:text-[#7E8DA5] font-semibold">Device Name:</span>
              <span class="font-bold text-slate-900 dark:text-[#F1F5F9]">{{ inspectingDevice.deviceName }}</span>
            </div>

            <div class="p-3 bg-slate-50 dark:bg-[#172235] rounded-xl flex justify-between items-center">
              <span class="text-slate-500 dark:text-[#7E8DA5] font-semibold">Assigned School:</span>
              <span class="font-medium text-slate-900 dark:text-[#F1F5F9]">{{ getSchoolName(inspectingDevice.schoolId) }}</span>
            </div>

            <div class="p-3 bg-slate-50 dark:bg-[#172235] rounded-xl flex justify-between items-center">
              <span class="text-slate-500 dark:text-[#7E8DA5] font-semibold">Chip ID:</span>
              <div class="flex items-center gap-2">
                <span class="font-mono text-slate-800 dark:text-[#F1F5F9]">{{ inspectingDevice.chipId || "None" }}</span>
                <button v-if="inspectingDevice.chipId" @click="copyText(inspectingDevice.chipId, 'Chip ID')" class="text-slate-400 hover:text-blue-600">
                  <Copy class="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            <div class="p-3 bg-slate-50 dark:bg-[#172235] rounded-xl flex justify-between items-center">
              <span class="text-slate-500 dark:text-[#7E8DA5] font-semibold">MAC Address:</span>
              <div class="flex items-center gap-2">
                <span class="font-mono text-slate-800 dark:text-[#F1F5F9]">{{ inspectingDevice.macAddress || "None" }}</span>
                <button v-if="inspectingDevice.macAddress" @click="copyText(inspectingDevice.macAddress, 'MAC Address')" class="text-slate-400 hover:text-blue-600">
                  <Copy class="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            <div class="p-3 bg-slate-50 dark:bg-[#172235] rounded-xl flex justify-between items-center">
              <span class="text-slate-500 dark:text-[#7E8DA5] font-semibold">Last Known IP:</span>
              <div class="flex items-center gap-2">
                <span class="font-mono text-slate-800 dark:text-[#F1F5F9]">{{ inspectingDevice.lastIp || "None" }}</span>
                <button v-if="inspectingDevice.lastIp" @click="copyText(inspectingDevice.lastIp, 'Last IP')" class="text-slate-400 hover:text-blue-600">
                  <Copy class="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            <div class="p-3 bg-slate-50 dark:bg-[#172235] rounded-xl flex justify-between items-center">
              <span class="text-slate-500 dark:text-[#7E8DA5] font-semibold">Pairing OTP:</span>
              <div class="flex items-center gap-2">
                <span class="font-mono font-bold text-slate-800 dark:text-[#F1F5F9]">{{ inspectingDevice.code || "None" }}</span>
                <button v-if="inspectingDevice.code" @click="copyText(inspectingDevice.code, 'Pairing Code')" class="text-slate-400 hover:text-blue-600">
                  <Copy class="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            <div class="p-3 bg-slate-50 dark:bg-[#172235] rounded-xl flex justify-between items-center">
              <span class="text-slate-500 dark:text-[#7E8DA5] font-semibold">Last Heartbeat Ping:</span>
              <span class="text-slate-800 dark:text-[#F1F5F9]">{{ formatDateFull(inspectingDevice.lastPing) }}</span>
            </div>

            <div class="p-3 bg-slate-50 dark:bg-[#172235] rounded-xl flex justify-between items-center">
              <span class="text-slate-500 dark:text-[#7E8DA5] font-semibold">Date Registered:</span>
              <span class="text-slate-800 dark:text-[#F1F5F9]">{{ formatDateFull(inspectingDevice.createdAt) }}</span>
            </div>
          </div>

          <div class="mt-5 flex gap-3">
            <button
              @click="closeInspectModal"
              class="w-full py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-[#18243A] dark:hover:bg-[#202e48] text-slate-700 dark:text-[#F1F5F9] rounded-xl text-xs font-semibold transition"
            >
              Close Diagnostics
            </button>
          </div>
        </div>
      </div>

      <!-- ================= MODAL 3: DIGITAL SMART ID PREVIEW ================= -->
      <div
        v-if="showCardModal && previewingCard"
        class="fixed inset-0 bg-slate-900/60 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 transition-all"
        @click.self="closeCardModal"
      >
        <div class="bg-white dark:bg-[#111C30] rounded-3xl shadow-2xl p-6 sm:p-7 w-full max-w-md border border-slate-200 dark:border-[#263449]">
          <div class="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-[#263449] mb-4">
            <span class="text-xs font-bold text-slate-400 uppercase tracking-wider">Official Digital Identity Card</span>
            <button @click="closeCardModal" class="text-slate-400 hover:text-slate-600 dark:hover:text-white p-1">
              <X class="w-5 h-5" />
            </button>
          </div>

          <!-- Digital Card Graphic Box -->
          <div class="relative bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 text-white rounded-2xl p-5 shadow-xl border border-white/10 overflow-hidden">
            <!-- Decorative Accent Waves -->
            <div class="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-blue-500/10 blur-2xl pointer-events-none"></div>
            <div class="absolute -left-8 -bottom-8 w-32 h-32 rounded-full bg-indigo-500/10 blur-2xl pointer-events-none"></div>

            <!-- Card Header -->
            <div class="flex items-center justify-between border-b border-white/10 pb-3 mb-4">
              <div class="flex items-center gap-2">
                <div class="w-7 h-7 rounded-lg bg-blue-600/80 flex items-center justify-center text-white">
                  <Building2 class="w-4 h-4" />
                </div>
                <div class="min-w-0">
                  <h4 class="text-xs font-bold truncate tracking-wide text-white">
                    {{ previewingCard.schoolName || "Institutional Smart Card" }}
                  </h4>
                  <span class="text-[9px] uppercase tracking-widest text-blue-300 font-semibold block">
                    Contactless Identity
                  </span>
                </div>
              </div>

              <!-- RFID Icon -->
              <div class="w-6 h-6 flex items-center justify-center text-blue-400/80">
                <Wifi class="w-4 h-4 rotate-90" />
              </div>
            </div>

            <!-- Card Body: Avatar & Core Info -->
            <div class="flex gap-4 items-center mb-4">
              <!-- Avatar -->
              <div class="w-20 h-20 rounded-2xl overflow-hidden ring-2 ring-blue-400/50 bg-white/10 flex items-center justify-center flex-shrink-0 shadow-lg">
                <img
                  v-if="previewingCard.photo && !cardImageErrors[previewingCard.smartCardId]"
                  :src="previewingCard.photo"
                  :alt="previewingCard.name"
                  class="w-full h-full object-cover"
                />
                <div
                  v-else
                  :style="{ backgroundColor: getAvatarColor(previewingCard.name) }"
                  class="w-full h-full flex items-center justify-center text-white font-black text-2xl uppercase"
                >
                  {{ getInitial(previewingCard.name) }}
                </div>
              </div>

              <div class="min-w-0 flex-1">
                <span
                  :class="[
                    'inline-block px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-wider mb-1',
                    previewingCard.role === 'student' ? 'bg-blue-600 text-white' : 'bg-violet-600 text-white',
                  ]"
                >
                  {{ previewingCard.role }}
                </span>
                <h3 class="text-base font-extrabold tracking-tight capitalize truncate text-white">
                  {{ previewingCard.name }}
                </h3>
                <p v-if="previewingCard.role === 'student' && previewingCard.className" class="text-xs text-blue-200 mt-0.5">
                  Class: {{ previewingCard.className }} (Roll: {{ previewingCard.rollNumber || "—" }})
                </p>
                <p v-else class="text-xs text-blue-200 mt-0.5">
                  Faculty Staff Member
                </p>
              </div>
            </div>

            <!-- Card Bottom Specs: Monospace Card ID & Barcode simulation -->
            <div class="pt-3 border-t border-white/10 flex items-end justify-between">
              <div>
                <span class="block text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Card ID / RFID</span>
                <span class="font-mono text-sm font-bold text-white tracking-widest">
                  #{{ previewingCard.smartCardId }}
                </span>
              </div>
              <div class="flex items-center gap-1.5">
                <span
                  :class="[
                    'px-2 py-0.5 rounded text-[9px] font-bold uppercase',
                    previewingCard.status === 'active'
                      ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-400/30'
                      : previewingCard.status === 'orderedAgain'
                      ? 'bg-purple-500/20 text-purple-300 border border-purple-400/30'
                      : previewingCard.status === 'inTransit'
                      ? 'bg-amber-500/20 text-amber-300 border border-amber-400/30'
                      : 'bg-rose-500/20 text-rose-300 border border-rose-400/30',
                  ]"
                >
                  {{ formatCardStatus(previewingCard.status) }}
                </span>
              </div>
            </div>
          </div>

          <!-- Cardholder Metadata Details List -->
          <div class="mt-4 space-y-2 text-xs">
            <div class="p-2.5 rounded-xl bg-slate-50 dark:bg-[#172235] flex justify-between items-center">
              <span class="text-slate-500 dark:text-[#7E8DA5]">Email:</span>
              <span class="font-medium text-slate-800 dark:text-[#F1F5F9]">{{ previewingCard.email || "No email registered" }}</span>
            </div>
            <div class="p-2.5 rounded-xl bg-slate-50 dark:bg-[#172235] flex justify-between items-center">
              <span class="text-slate-500 dark:text-[#7E8DA5]">Phone:</span>
              <span class="font-medium text-slate-800 dark:text-[#F1F5F9]">{{ previewingCard.phone || "No phone registered" }}</span>
            </div>
            <div v-if="previewingCard.dob" class="p-2.5 rounded-xl bg-slate-50 dark:bg-[#172235] flex justify-between items-center">
              <span class="text-slate-500 dark:text-[#7E8DA5]">Date of Birth:</span>
              <span class="font-medium text-slate-800 dark:text-[#F1F5F9]">{{ formatDateShort(previewingCard.dob) }}</span>
            </div>
          </div>

          <!-- Re-ordered by admin notice box -->
          <div
            v-if="previewingCard.status === 'orderedAgain' || previewingCard.isReordered"
            class="mt-3 p-3 rounded-xl bg-purple-50 dark:bg-purple-950/40 border border-purple-200 dark:border-purple-800/50 flex items-start gap-2.5 text-xs text-purple-900 dark:text-purple-200"
          >
            <RotateCw class="w-4 h-4 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong class="font-bold block">Re-ordered by Admin</strong>
              <span class="text-[11px] text-purple-700 dark:text-purple-300">
                This card is ordered again by the admin.
              </span>
            </div>
          </div>

          <!-- Modal Action -->
          <div class="mt-5 flex gap-3">
            <button
              @click="closeCardModal"
              class="w-full py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-[#18243A] dark:hover:bg-[#202e48] text-slate-700 dark:text-[#F1F5F9] rounded-xl text-xs font-semibold transition"
            >
              Done
            </button>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted, watch } from "vue";
  import { useStore } from "vuex";
  import {
    Cpu,
    CreditCard,
    Search,
    Building2,
    RotateCw,
    CheckCircle,
    XCircle,
    Clock,
    Wifi,
    Copy,
    Check,
    Edit3,
    Eye,
    GraduationCap,
    UserCog,
    Truck,
    ChevronLeft,
    ChevronRight,
    Mail,
    Phone,
    Hash,
    BookOpen,
    Filter,
    AlertCircle,
    X,
    ShieldCheck,
  } from "lucide-vue-next";
  import { useGlobalToast } from "@/utils/useGlobalToast";

  /* ================= STORE & TOAST ================= */
  const store = useStore();
  const { showToast } = useGlobalToast();

  /* ================= NAVIGATION TABS ================= */
  const activeTab = ref("devices"); // 'devices' | 'smartcards'
  const selectedSchoolId = ref("");
  const errorMessage = ref("");
  const copiedItem = ref("");
  const lastSyncTime = ref(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));

  const switchTab = (tab) => {
    activeTab.value = tab;
    errorMessage.value = "";
    if (tab === "devices" && devices.value.length === 0) {
      fetchDevices();
    } else if (tab === "smartcards" && smartCards.value.length === 0) {
      fetchSmartCards();
    }
  };

  /* ================= SCHOOLS ================= */
  const schools = computed(() => store.state.schoolsBasic || []);

  const selectedSchoolName = computed(() => {
    if (!selectedSchoolId.value) return "All Registered Schools";
    const found = schools.value.find((s) => s._id === selectedSchoolId.value);
    return found ? found.schoolName : "Specific School";
  });

  const getSchoolName = (school) => {
    if (!school) return "Unassigned";
    if (typeof school === "object" && school.schoolName) {
      return school.schoolName;
    }
    const found = schools.value.find((s) => s._id === school);
    return found ? found.schoolName : "School " + String(school).slice(-4);
  };

  const handleSchoolChange = () => {
    deviceCurrentPage.value = 1;
    cardCurrentPage.value = 1;
    if (activeTab.value === "devices") {
      fetchDevices();
    } else {
      fetchSmartCards();
    }
  };

  const refreshActiveData = async () => {
    lastSyncTime.value = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    if (activeTab.value === "devices") {
      await fetchDevices();
    } else {
      await fetchSmartCards();
    }
    showToast("success", "Refreshed operational records");
  };

  /* ================= DEVICES LOGIC ================= */
  const deviceCurrentPage = ref(1);
  const deviceLimit = ref(20);
  const deviceSearchQuery = ref("");
  const deviceStatusFilter = ref("");
  const deviceOnlineFilter = ref("all");
  const isLoadingDevices = ref(false);

  let deviceSearchTimeout = null;

  const devices = computed(() => store.state.allDevices || []);
  const totalDevices = computed(() => store.state.allDevicesTotal || 0);

  const totalDevicePages = computed(() => {
    return Math.max(1, Math.ceil(totalDevices.value / deviceLimit.value));
  });

  const isDeviceOnline = (device) => {
    if (device.isOnline !== undefined) return Boolean(device.isOnline);
    if (!device.lastPing) return false;
    const now = Date.now();
    const pingTime = new Date(device.lastPing).getTime();
    return now - pingTime < 2 * 60 * 1000; // 2 minutes threshold
  };

  const filteredDevices = computed(() => {
    let list = devices.value;
    if (deviceOnlineFilter.value === "online") {
      list = list.filter((d) => isDeviceOnline(d));
    } else if (deviceOnlineFilter.value === "offline") {
      list = list.filter((d) => !isDeviceOnline(d));
    }
    return list;
  });

  const deviceStats = computed(() => {
    const list = devices.value;
    return {
      total: totalDevices.value || list.length,
      online: list.filter((d) => isDeviceOnline(d)).length,
      active: list.filter((d) => d.status === "active").length,
      pending: list.filter((d) => d.status === "pending").length,
      disabled: list.filter((d) => d.status === "disabled").length,
    };
  });

  const fetchDevices = async () => {
    isLoadingDevices.value = true;
    errorMessage.value = "";
    try {
      const params = {
        page: deviceCurrentPage.value,
        limit: deviceLimit.value,
        search: deviceSearchQuery.value.trim(),
      };
      if (selectedSchoolId.value) {
        params.schoolId = selectedSchoolId.value;
      }
      if (deviceStatusFilter.value) {
        params.status = deviceStatusFilter.value;
      }
      await store.dispatch("fetchAllDevices", params);
    } catch (err) {
      errorMessage.value = err.response?.data?.message || err.message || "Failed to load devices";
    } finally {
      isLoadingDevices.value = false;
    }
  };

  const handleDeviceSearch = () => {
    if (deviceSearchTimeout) clearTimeout(deviceSearchTimeout);
    deviceSearchTimeout = setTimeout(() => {
      deviceCurrentPage.value = 1;
      fetchDevices();
    }, 350);
  };

  const clearDeviceSearch = () => {
    deviceSearchQuery.value = "";
    deviceCurrentPage.value = 1;
    fetchDevices();
  };

  const handleDeviceFilterChange = () => {
    deviceCurrentPage.value = 1;
    fetchDevices();
  };

  const resetDeviceFilters = () => {
    deviceSearchQuery.value = "";
    deviceStatusFilter.value = "";
    deviceOnlineFilter.value = "all";
    deviceCurrentPage.value = 1;
    fetchDevices();
  };

  const changeDevicePage = (page) => {
    if (page >= 1 && page <= totalDevicePages.value) {
      deviceCurrentPage.value = page;
      fetchDevices();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  /* ================= SMART CARDS LOGIC ================= */
  const cardCurrentPage = ref(1);
  const cardLimit = ref(20);
  const cardSearchQuery = ref("");
  const cardRoleFilter = ref("");
  const cardStatusFilter = ref("");
  const isLoadingCards = ref(false);
  const cardImageErrors = ref({});

  let cardSearchTimeout = null;

  const smartCards = computed(() => store.state.smartCards || []);
  const cardPagination = computed(() => store.state.cardsPagination || { page: 1, limit: 20, total: 0, pages: 1 });
  const totalCardsCount = computed(() => cardPagination.value?.total || 0);
  const totalCardPages = computed(() => cardPagination.value?.pages || 1);

  const cardStats = computed(() => {
    const all = smartCards.value;
    return {
      total: totalCardsCount.value,
      active: all.filter((c) => c.status === "active").length,
      inTransit: all.filter((c) => c.status === "inTransit").length,
      orderedAgain: all.filter((c) => c.status === "orderedAgain" || c.isReordered).length,
      disabled: all.filter((c) => c.status === "disabled").length,
      students: all.filter((c) => c.role === "student").length,
      teachers: all.filter((c) => c.role === "teacher").length,
    };
  });

  const fetchSmartCards = async () => {
    isLoadingCards.value = true;
    errorMessage.value = "";
    try {
      const payload = {
        page: cardCurrentPage.value,
        limit: cardLimit.value,
        search: cardSearchQuery.value.trim(),
        schoolId: selectedSchoolId.value || null,
        role: cardRoleFilter.value || null,
        status: cardStatusFilter.value || null,
      };
      await store.dispatch("fetchAllSmartCards", payload);
    } catch (err) {
      errorMessage.value = err.response?.data?.message || err.message || "Failed to load smart cards";
    } finally {
      isLoadingCards.value = false;
    }
  };

  const handleCardSearch = () => {
    if (cardSearchTimeout) clearTimeout(cardSearchTimeout);
    cardSearchTimeout = setTimeout(() => {
      cardCurrentPage.value = 1;
      fetchSmartCards();
    }, 350);
  };

  const clearCardSearch = () => {
    cardSearchQuery.value = "";
    cardCurrentPage.value = 1;
    fetchSmartCards();
  };

  const handleCardFilterChange = () => {
    cardCurrentPage.value = 1;
    fetchSmartCards();
  };

  const resetCardFilters = () => {
    cardSearchQuery.value = "";
    cardRoleFilter.value = "";
    cardStatusFilter.value = "";
    cardCurrentPage.value = 1;
    fetchSmartCards();
  };

  const changeCardPage = (page) => {
    if (page >= 1 && page <= totalCardPages.value) {
      cardCurrentPage.value = page;
      fetchSmartCards();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  /* ================= MODALS & ACTIONS ================= */
  const showEditModal = ref(false);
  const isSaving = ref(false);
  const editingDeviceId = ref(null);
  const editForm = ref({
    deviceName: "",
    code: "",
    status: "active",
    studentURL: "",
    teacherURL: "",
    isActive: true,
  });

  watch(
    () => editForm.value.status,
    (newStatus) => {
      if (newStatus === "disabled") {
        editForm.value.isActive = false;
      }
    }
  );

  watch(
    () => editForm.value.isActive,
    (newIsActive) => {
      if (!newIsActive) {
        editForm.value.status = "disabled";
      }
    }
  );

  const openEditModal = (device) => {
    editingDeviceId.value = device._id;
    editForm.value = {
      deviceName: device.deviceName || "",
      code: device.code || "",
      status: device.status || "active",
      studentURL: device.studentURL || device.url || "",
      teacherURL: device.teacherURL || device.url || "",
      isActive: Boolean(device.isActive),
    };
    showEditModal.value = true;
  };

  const closeEditModal = () => {
    showEditModal.value = false;
    editingDeviceId.value = null;
  };

  const saveDevice = async () => {
    isSaving.value = true;
    errorMessage.value = "";
    try {
      await store.dispatch("editDevice", {
        deviceId: editingDeviceId.value,
        updates: {
          deviceName: editForm.value.deviceName,
          status: editForm.value.status,
          studentURL: editForm.value.studentURL,
          teacherURL: editForm.value.teacherURL,
          isActive: editForm.value.isActive,
        },
      });
      showToast("success", "Device configuration updated successfully");
      closeEditModal();
      await fetchDevices();
    } catch (err) {
      errorMessage.value = err.response?.data?.message || err.message || "Failed to update device";
      showToast("error", errorMessage.value);
    } finally {
      isSaving.value = false;
    }
  };

  // Inspect Device Modal
  const showInspectModal = ref(false);
  const inspectingDevice = ref(null);

  const openInspectModal = (device) => {
    inspectingDevice.value = device;
    showInspectModal.value = true;
  };

  const closeInspectModal = () => {
    showInspectModal.value = false;
    inspectingDevice.value = null;
  };

  // Smart Card Preview Modal
  const showCardModal = ref(false);
  const previewingCard = ref(null);

  const openCardModal = (card) => {
    previewingCard.value = card;
    showCardModal.value = true;
  };

  const closeCardModal = () => {
    showCardModal.value = false;
    previewingCard.value = null;
  };

  /* ================= HELPERS & FORMATTERS ================= */
  const isLoading = computed(() => isLoadingDevices.value || isLoadingCards.value);

  const copyText = async (text, label) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(String(text));
      copiedItem.value = String(text);
      showToast("info", `Copied ${label} to clipboard`);
      setTimeout(() => {
        if (copiedItem.value === String(text)) copiedItem.value = "";
      }, 2000);
    } catch {
      showToast("error", "Failed to copy to clipboard");
    }
  };

  const getStatusBadgeClass = (status) => {
    const map = {
      active: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-400",
      pending: "bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-400",
      disabled: "bg-rose-100 text-rose-800 dark:bg-rose-950/50 dark:text-rose-400",
      transferred: "bg-purple-100 text-purple-800 dark:bg-purple-950/50 dark:text-purple-400",
    };
    return map[status] || "bg-slate-100 text-slate-800 dark:bg-[#18243A] dark:text-[#A6B4C8]";
  };

  const formatCardStatus = (status) => {
    if (!status) return "Unknown";
    if (status === "orderedAgain") return "Ordered Again";
    if (status === "inTransit") return "In Transit";
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatDateFull = (dateString) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleString("en-US", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return "Never";
    const deltaSeconds = Math.floor((Date.now() - new Date(dateString).getTime()) / 1000);
    if (deltaSeconds < 30) return "Just now";
    if (deltaSeconds < 60) return `${deltaSeconds}s ago`;
    const minutes = Math.floor(deltaSeconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const getAvatarColor = (name) => {
    if (!name) return "#3B82F6";
    const colors = ["#2563EB", "#7C3AED", "#059669", "#D97706", "#DC2626", "#0891B2"];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const getInitial = (name) => {
    if (!name) return "U";
    return name.trim().charAt(0).toUpperCase();
  };

  /* ================= LIFECYCLE ================= */
  onMounted(async () => {
    store.dispatch("fetchAllSchoolsBasic");
    await fetchDevices();
    fetchSmartCards();
  });
</script>
